import { useEffect } from 'react';
import {
  useParams,
  generatePath,
  Outlet,
  useMatch,
  useNavigate,
} from 'react-router-dom';
import { PermissionDenied, PermissionEnum, IconLoader, Tabs } from '@ui/src';

import { useAppSelector } from '@store';
import {
  useLazyGetCustomersPspPartnersQuery,
  useLazyGetCustomersPspIntegrationsQuery,
} from '../store/services/customers';
import { OrganizationType } from '../types';
import { useLazyGetCustomersCASPQuery } from '../../../store/dictionaries/services';

import ROUTES from '../../../routes';

import cn from '../customers.module.css';

const ExternalAccounts = ({ type }: { type: OrganizationType }) => {
  const individualMatch = useMatch(
    ROUTES.CUSTOMERS_INDIVIDUAL_EXTERNAL_ACCOUNTS,
  );
  const corporateMatch = useMatch(ROUTES.CUSTOMERS_CORPORATE_EXTERNAL_ACCOUNTS);

  const { activePermissions, casp } = useAppSelector(
    (state) => state.customersDictionaries,
  );

  const { pspPartners, openPayNotificationVisible, pspIntegrations } =
    useAppSelector((state) => state.customersExternalAccounts);

  const { customerId } = useParams();

  const navigate = useNavigate();

  const [getPspPartners] = useLazyGetCustomersPspPartnersQuery();
  const [getCustomersCasp] = useLazyGetCustomersCASPQuery();
  const [getPspIntegrations] = useLazyGetCustomersPspIntegrationsQuery();

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.PSP_LIST) &&
      !pspIntegrations
    ) {
      getPspIntegrations();
    }
  }, [activePermissions, pspIntegrations]);

  useEffect(() => {
    if (!pspPartners) {
      getPspPartners();
    }
  }, [pspPartners]);

  useEffect(() => {
    if (!casp) {
      getCustomersCasp();
    }
  }, [casp]);

  useEffect(() => {
    if (customerId) {
      if (type === OrganizationType.INDIVIDUAL && individualMatch) {
        if (
          activePermissions?.includes(
            PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST,
          )
        ) {
          navigate(
            generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_EXTERNAL_ACCOUNTS_LIST, {
              customerId,
            }),
          );
        } else if (
          !activePermissions?.includes(
            PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST,
          ) &&
          activePermissions?.includes(
            PermissionEnum.CUSTOMERS_EXTERNAL_ORGANIZATIONS_LIST,
          )
        ) {
          navigate(
            generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_EXTERNAL_LINKED_CLIENTS, {
              customerId,
            }),
          );
        }
      } else if (type === OrganizationType.CORPORATE && corporateMatch) {
        if (
          activePermissions?.includes(
            PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST,
          )
        ) {
          navigate(
            generatePath(ROUTES.CUSTOMERS_CORPORATE_EXTERNAL_ACCOUNTS_LIST, {
              customerId,
            }),
          );
        } else if (
          !activePermissions?.includes(
            PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST,
          ) &&
          activePermissions?.includes(
            PermissionEnum.CUSTOMERS_EXTERNAL_ORGANIZATIONS_LIST,
          )
        ) {
          navigate(
            generatePath(ROUTES.CUSTOMERS_CORPORATE_EXTERNAL_LINKED_CLIENTS, {
              customerId,
            }),
          );
        }
      }
    }
  }, [type, individualMatch, corporateMatch, customerId]);

  let content = <div />;

  if (
    activePermissions &&
    !activePermissions.includes(PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST) &&
    !activePermissions.includes(
      PermissionEnum.CUSTOMERS_EXTERNAL_ORGANIZATIONS_LIST,
    )
  ) {
    content = <PermissionDenied />;
  } else if (
    activePermissions &&
    (activePermissions?.includes(PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST) ||
      activePermissions.includes(
        PermissionEnum.CUSTOMERS_EXTERNAL_ORGANIZATIONS_LIST,
      ))
  ) {
    content = (
      <div className={cn.externalAccounts}>
        <div className={cn.externalAccountsTop}>
          <div className={cn.externalAccountsTopTitle}>
            External Integration
          </div>
          {activePermissions.includes(
            PermissionEnum.CUSTOMERS_EXTERNAL_ORGANIZATIONS_LIST,
          ) &&
            activePermissions.includes(
              PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST,
            ) && (
              <Tabs
                wrapperClassName={cn.externalAccountsTabs}
                tabs={[
                  {
                    name: 'Accounts',
                    path: generatePath(
                      type === OrganizationType.INDIVIDUAL
                        ? ROUTES.CUSTOMERS_INDIVIDUAL_EXTERNAL_ACCOUNTS_LIST
                        : ROUTES.CUSTOMERS_CORPORATE_EXTERNAL_ACCOUNTS_LIST,
                      { customerId: customerId as string },
                    ),
                  },
                  {
                    name: 'Linked Customers',
                    path: generatePath(
                      type === OrganizationType?.INDIVIDUAL
                        ? ROUTES.CUSTOMERS_INDIVIDUAL_EXTERNAL_LINKED_CLIENTS
                        : ROUTES.CUSTOMERS_CORPORATE_EXTERNAL_LINKED_CLIENTS,
                      { customerId: customerId as string },
                    ),
                  },
                ]}
                enableRouting
              />
            )}
        </div>
        <Outlet />
        {openPayNotificationVisible && (
          <div className={cn.reportNotification}>
            <div className={cn.reportNotificationLeft}>
              <IconLoader />
              <div className={cn.reportText}>Account will be created soon</div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return content;
};

export default ExternalAccounts;
